import { cn } from '@utazzo/ui';
import React from 'react';
import { Link } from 'react-router-dom';

import type { LucideIcon } from 'lucide-react';

/**
 * KPI stat cards shown above each report tab: tinted icon square, big
 * tabular-nums value and a muted label. Linkable cards get a hover lift.
 */

export type KpiTone = 'default' | 'primary' | 'success' | 'warning' | 'danger';

const toneClasses: Record<KpiTone, string> = {
  default: 'bg-muted text-muted-foreground',
  primary: 'bg-primary/10 text-primary',
  success: 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400',
  warning: 'bg-amber-500/10 text-amber-600 dark:text-amber-400',
  danger: 'bg-rose-500/10 text-rose-600 dark:text-rose-400',
};

interface KpiCardProps {
  label: string;
  value: React.ReactNode;
  icon: LucideIcon;
  tone?: KpiTone;
  /** Optional small line under the label (e.g. a ratio or comparison). */
  hint?: React.ReactNode;
  /** Renders the card as a link. */
  to?: string;
  /** Renders the card as a button (drill-in). Ignored when `to` is set. */
  onClick?: () => void;
  /** Tighter padding / type scale for dense section grids. */
  compact?: boolean;
  /** Extra classes on the card root (e.g. grid col-span). */
  className?: string;
}

export const KpiCard = ({
  label,
  value,
  icon: Icon,
  tone = 'default',
  hint,
  to,
  onClick,
  compact = false,
  className,
}: KpiCardProps): React.JSX.Element => {
  // Same structure as KpiPairCard: header (icon + label) -> divider -> value,
  // so single and grouped tiles read identically.
  const inner = (
    <>
      <div className="flex items-center gap-2">
        <div
          className={cn(
            'flex shrink-0 items-center justify-center rounded-md',
            compact ? 'h-5 w-5' : 'h-6 w-6',
            toneClasses[tone],
          )}
        >
          <Icon className={compact ? 'h-3 w-3' : 'h-3.5 w-3.5'} />
        </div>
        <div
          className={cn(
            'text-muted-foreground truncate font-medium',
            compact ? 'text-xs' : 'text-sm',
          )}
        >
          {label}
        </div>
      </div>
      <div
        className={cn(
          tone === 'danger' ? 'border-rose-500/20' : 'border-border/60',
          'border-t',
          compact ? 'pt-1' : 'pt-1.5',
        )}
      >
        <div
          className={cn(
            'text-foreground truncate font-semibold tracking-tight tabular-nums',
            compact ? 'text-lg' : 'text-xl',
          )}
        >
          {value}
        </div>
        {hint !== undefined && hint !== null && (
          <div className="text-muted-foreground/70 truncate text-[11px] leading-tight">{hint}</div>
        )}
      </div>
    </>
  );
  const baseClass = cn(
    'border-border bg-card flex flex-col rounded-xl border shadow-sm',
    compact ? 'gap-1 p-2' : 'gap-1.5 p-2.5',
    className,
  );
  const hoverClass = 'cursor-pointer hover:border-primary/40 transition-all hover:shadow-md';
  if (to) {
    return (
      <Link to={to} className={cn(baseClass, hoverClass)}>
        {inner}
      </Link>
    );
  }
  if (onClick) {
    return (
      <button type="button" onClick={onClick} className={cn(baseClass, hoverClass, 'text-left')}>
        {inner}
      </button>
    );
  }
  return <div className={baseClass}>{inner}</div>;
};

/**
 * Paired stat tile: one card (the width of two KpiCards) with a title header
 * and N sub-stats below a divider. Collapses the old "Current X" + "MTD X"
 * two-tile pattern into a single, less-fragmented block.
 */
export const KpiPairCard = ({
  label,
  icon: Icon,
  tone = 'default',
  parts,
  className,
  onClick,
  compact = false,
}: {
  label: string;
  icon: LucideIcon;
  tone?: KpiTone;
  parts: Array<{ label: string; value: React.ReactNode; hint?: React.ReactNode }>;
  className?: string;
  /** Renders the card as a button (drill-in). */
  onClick?: () => void;
  /** Tighter padding / type scale for dense section grids. */
  compact?: boolean;
}): React.JSX.Element => {
  const Root = onClick ? 'button' : 'div';
  return (
    <Root
      {...(onClick ? { type: 'button' as const, onClick } : {})}
      className={cn(
        'border-border bg-card flex flex-col rounded-xl border text-left shadow-sm',
        compact ? 'gap-1 p-2' : 'gap-1.5 p-2.5',
        onClick && 'hover:border-primary/40 cursor-pointer transition-all hover:shadow-md',
        className,
      )}
    >
      <div className="flex items-center gap-2">
        <div
          className={cn(
            'flex shrink-0 items-center justify-center rounded-md',
            compact ? 'h-5 w-5' : 'h-6 w-6',
            toneClasses[tone],
          )}
        >
          <Icon className={compact ? 'h-3 w-3' : 'h-3.5 w-3.5'} />
        </div>
        <div
          className={cn(
            'text-muted-foreground truncate font-medium',
            compact ? 'text-xs' : 'text-sm',
          )}
        >
          {label}
        </div>
      </div>
      <div
        className={cn(
          'grid border-t',
          compact ? 'gap-2 pt-1' : 'gap-3 pt-1.5',
          parts.length >= 4 ? 'grid-cols-4' : parts.length === 3 ? 'grid-cols-3' : 'grid-cols-2',
          tone === 'danger' ? 'border-rose-500/20' : 'border-border/60',
        )}
      >
        {parts.map((part, index) => (
          <div key={index} className="min-w-0">
            <div
              className={cn(
                'text-foreground font-semibold tracking-tight tabular-nums',
                compact ? 'text-lg' : 'text-xl',
              )}
            >
              {part.value}
            </div>
            <div className="text-muted-foreground truncate text-xs leading-tight">{part.label}</div>
            {part.hint !== undefined && part.hint !== null && (
              <div className="text-muted-foreground/70 truncate text-[11px] leading-tight">
                {part.hint}
              </div>
            )}
          </div>
        ))}
      </div>
    </Root>
  );
};

/** Responsive grid wrapper for a row of KPI cards. */
export const KpiGrid = ({
  children,
  cols = 5,
}: {
  children: React.ReactNode;
  /** Max columns on wide screens (default 5). Use 6 for a 6-card one-row strip. */
  cols?: 5 | 6;
}): React.JSX.Element => (
  <div
    className={cn(
      'grid grid-cols-2 gap-3 md:grid-cols-3',
      cols === 6 ? 'xl:grid-cols-6' : 'xl:grid-cols-5',
    )}
  >
    {children}
  </div>
);
